// Luxury Intent Scoring Engine
// Client-side weighted scoring for HNW behavioral intelligence
// Feeds GA4 enrichment and server-side BoatData payloads

const STORAGE_KEY = 'yv_luxury_intent';
const DECAY_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Signal weights for luxury intent scoring
export const SIGNAL_WEIGHTS = {
  superyacht_valuation: 30,    // >$1M valuation
  luxury_valuation: 20,        // >$250K valuation
  cash_offer_click: 35,
  repeat_visit_7d: 15,
  repeat_visit_30d: 10,
  spec_download: 10,
  builder_search_luxury: 10,
  off_market_request: 40,
  document_upload: 25,
  brokerage_inquiry: 30,
  concierge_inquiry: 30,
  net_proceeds_use: 15,
  trade_up_planner: 20,
  time_on_site_3min: 5,
  scroll_depth_75: 5,
  multiple_yachts_same_tier: 10,
} as const;

export type SignalType = keyof typeof SIGNAL_WEIGHTS;

export type IntentTier = 'researching' | 'active' | 'motivated' | 'hnw_urgent';

interface StoredIntentData {
  score: number;
  signals: Partial<Record<SignalType, number>>; // signal -> count
  lastActivityAt: number; // timestamp
  lastDecayAt: number; // timestamp
  tier: IntentTier;
}

/**
 * Get intent tier from score
 */
export function getIntentTier(score: number): IntentTier {
  if (score >= 100) return 'hnw_urgent';
  if (score >= 71) return 'motivated';
  if (score >= 31) return 'active';
  return 'researching';
}

/**
 * Load stored intent data from localStorage
 */
function loadIntentData(): StoredIntentData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored) as StoredIntentData;
      return applyDecay(data);
    }
  } catch {}
  return {
    score: 0,
    signals: {},
    lastActivityAt: Date.now(),
    lastDecayAt: Date.now(),
    tier: 'researching',
  };
}

/**
 * Save intent data to localStorage
 */
function saveIntentData(data: StoredIntentData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/**
 * Apply 50% score decay if 30+ days of inactivity
 */
function applyDecay(data: StoredIntentData): StoredIntentData {
  const now = Date.now();
  const timeSinceActivity = now - data.lastActivityAt;
  
  if (timeSinceActivity >= DECAY_INTERVAL_MS) {
    const decayPeriods = Math.floor(timeSinceActivity / DECAY_INTERVAL_MS);
    const decayFactor = Math.pow(0.5, decayPeriods);
    const decayedScore = Math.round(data.score * decayFactor);
    
    return {
      ...data,
      score: decayedScore,
      tier: getIntentTier(decayedScore),
      lastDecayAt: now,
    };
  }
  
  return data;
}

/**
 * Record a luxury signal and update the intent score
 * Returns the new score and tier
 */
export function recordSignal(signal: SignalType): { score: number; tier: IntentTier; previousTier: IntentTier } {
  const data = loadIntentData();
  const previousTier = data.tier;
  
  // Add signal weight
  const weight = SIGNAL_WEIGHTS[signal];
  data.score += weight;
  data.signals[signal] = (data.signals[signal] || 0) + 1;
  data.lastActivityAt = Date.now();
  data.tier = getIntentTier(data.score);
  
  saveIntentData(data);
  
  return { score: data.score, tier: data.tier, previousTier };
}

/**
 * Get the current intent score and tier without modifying it
 */
export function getCurrentIntent(): { score: number; tier: IntentTier; signals: Partial<Record<SignalType, number>> } {
  const data = loadIntentData();
  return { score: data.score, tier: data.tier, signals: data.signals };
}

/**
 * Check if a tier escalation happened (for triggering intent_escalation events)
 */
export function didTierEscalate(previousTier: IntentTier, currentTier: IntentTier): boolean {
  const tierOrder: IntentTier[] = ['researching', 'active', 'motivated', 'hnw_urgent'];
  return tierOrder.indexOf(currentTier) > tierOrder.indexOf(previousTier);
}

/**
 * Check for returning visitor signals based on localStorage timestamps
 */
export function checkReturningVisitor(): { isRepeat7d: boolean; isRepeat30d: boolean } {
  const VISIT_KEY = 'yv_last_visit';
  const now = Date.now();
  
  try {
    const lastVisit = localStorage.getItem(VISIT_KEY);
    localStorage.setItem(VISIT_KEY, now.toString());
    
    if (!lastVisit) {
      return { isRepeat7d: false, isRepeat30d: false };
    }
    
    const elapsed = now - parseInt(lastVisit, 10);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    
    return {
      isRepeat7d: elapsed <= sevenDays && elapsed > 0,
      isRepeat30d: elapsed <= thirtyDays && elapsed > sevenDays,
    };
  } catch {
    return { isRepeat7d: false, isRepeat30d: false };
  }
}

/**
 * Predicted close window in days based on intent tier
 */
export function getPredictedCloseWindowDays(tier: IntentTier): number {
  switch (tier) {
    case 'hnw_urgent': return 30;
    case 'motivated': return 60;
    case 'active': return 120;
    default: return 365;
  }
}
